import React from "react";
import { VECTOR_CONTROL_TABS } from "./VectorControlTabs";

/**
 * Hook for managing vector control tab state
 * @param {string} initialTab - Initial active tab
 * @returns {object} - Tab state and handlers
 */
export const useVectorControlTabs = (
  initialTab = VECTOR_CONTROL_TABS[0].key
) => {
  const [activeTab, setActiveTab] = React.useState(initialTab);

  const handleTabChange = React.useCallback((tabKey) => {
    setActiveTab(tabKey);
  }, []);

  const getActiveTabData = React.useCallback(() => {
    return VECTOR_CONTROL_TABS.find((tab) => tab.key === activeTab);
  }, [activeTab]);

  const isKelambuType = React.useCallback(
    (tabKey = activeTab) => {
      const tab = VECTOR_CONTROL_TABS.find((t) => t.key === tabKey);
      return tab?.type === "kelambu";
    },
    [activeTab]
  );

  const isIRSType = React.useCallback(
    (tabKey = activeTab) => {
      const tab = VECTOR_CONTROL_TABS.find((t) => t.key === tabKey);
      return tab?.type === "irs";
    },
    [activeTab]
  );

  const getTabsByType = React.useCallback((type) => {
    return VECTOR_CONTROL_TABS.filter((tab) => tab.type === type);
  }, []);

  return {
    activeTab,
    setActiveTab,
    handleTabChange,
    getActiveTabData,
    isKelambuType,
    isIRSType,
    getTabsByType,
    allTabs: VECTOR_CONTROL_TABS,
  };
};

/**
 * Utility functions for vector control types
 */
export const VectorControlUtils = {
  // Check if a tab key is a Kelambu type
  isKelambuType: (tabKey) => {
    const tab = VECTOR_CONTROL_TABS.find((t) => t.key === tabKey);
    return tab?.type === "kelambu";
  },

  // Check if a tab key is IRS type
  isIRSType: (tabKey) => {
    const tab = VECTOR_CONTROL_TABS.find((t) => t.key === tabKey);
    return tab?.type === "irs";
  },

  // Get all Kelambu type tabs
  getKelambuTabs: () => {
    return VECTOR_CONTROL_TABS.filter((tab) => tab.type === "kelambu");
  },

  // Get IRS tab
  getIRSTab: () => {
    return VECTOR_CONTROL_TABS.find((tab) => tab.type === "irs");
  },

  // Get tab data by key
  getTabData: (tabKey) => {
    return VECTOR_CONTROL_TABS.find((tab) => tab.key === tabKey);
  },

  // Get all tab keys
  getAllTabKeys: () => {
    return VECTOR_CONTROL_TABS.map((tab) => tab.key);
  },

  // Get tab type by key
  getTabType: (tabKey) => {
    const tab = VECTOR_CONTROL_TABS.find((t) => t.key === tabKey);
    return tab?.type;
  },

  // Validate if tab key exists
  isValidTabKey: (tabKey) => {
    return VECTOR_CONTROL_TABS.some((tab) => tab.key === tabKey);
  },
};

export default VectorControlUtils;
